import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ReactComponent as EditIcon } from "../../assets/icons/tables/edit-icon.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { ICommunity, useCommunities } from "../../store/useCommunities";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function DragHandle(props: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 select-none px-1"
      title="Drag to reorder"
    >
      ⠿
    </div>
  );
}

function PinToggle({
  community,
  selectedCity,
  onToggle,
}: {
  community: ICommunity;
  selectedCity: string;
  onToggle: () => void;
}) {
  // Pin only makes sense for a city this community is actually eligible in -
  // pinning it for a city it never shows in would be a silent no-op.
  const isEligible = (community.cities ?? []).includes(selectedCity);
  if (!isEligible) {
    return (
      <span
        className="text-gray-300 select-none"
        title={`Not shown in ${selectedCity}`}
      >
        -
      </span>
    );
  }
  const isPinned = (community.pinnedCities ?? []).includes(selectedCity);
  return (
    <button
      type="button"
      onClick={onToggle}
      title={
        isPinned
          ? `Trending in ${selectedCity} - click to unpin`
          : `Pin as trending in ${selectedCity}`
      }
      className={`text-xl leading-none ${
        isPinned ? "text-droppie-oranje" : "text-gray-300 hover:text-gray-400"
      }`}
    >
      {isPinned ? "★" : "☆"}
    </button>
  );
}

function SortableRow({
  community,
  selectedCity,
  onTogglePin,
  onToggleDonators,
  onOpenDetail,
  onEdit,
  onDelete,
}: {
  community: ICommunity;
  selectedCity: string;
  onTogglePin: () => void;
  onToggleDonators: () => void;
  onOpenDetail: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: community._id as string });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="bg-white hover:bg-gray-100 transition ease-linear border-b"
    >
      <td className="px-2 py-1 sm:py-4 w-8">
        <DragHandle {...attributes} {...listeners} />
      </td>
      <td className="px-2 sm:px-6 py-1 sm:py-4 text-center">
        <PinToggle
          community={community}
          selectedCity={selectedCity}
          onToggle={onTogglePin}
        />
      </td>
      <td
        onClick={onOpenDetail}
        className="px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap cursor-pointer"
      >
        {community.name}
      </td>
      <td onClick={onOpenDetail} className="px-2 sm:px-6 py-1 sm:py-4 cursor-pointer">
        {Number(
          (community?.totalRevenue ? community?.totalRevenue / 100 : 0)?.toFixed(2)
        )}{" "}
        €
      </td>
      <td onClick={onOpenDetail} className="px-2 sm:px-6 py-1 sm:py-4 cursor-pointer">
        {community?.cities?.join(", ")}
      </td>
      <td className="px-2 sm:px-6 py-1 sm:py-4">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={community.showDonators !== false}
            onChange={onToggleDonators}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500" />
        </label>
      </td>
      <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
        <EditIcon onClick={onEdit} className="mr-7" />
        <TrashIcon onClick={onDelete} />
      </td>
    </tr>
  );
}

export const CommunitiesTable = () => {
  /** Hooks */
  const { communities, deleteCommunity, updateCommunity, reorderCommunities, togglePin } =
    useCommunities();
  const navigate = useNavigate();
  const sensors = useSensors(useSensor(PointerSensor));

  // Every city any community lists itself under - pin is per-city, so this
  // drives which city's star column is currently shown/editable. Order is
  // global, so it isn't affected by this selection.
  const availableCities = useMemo(() => {
    const set = new Set<string>();
    communities.forEach((c) => (c.cities ?? []).forEach((city) => set.add(city)));
    return Array.from(set).sort();
  }, [communities]);

  const [selectedCity, setSelectedCity] = useState<string>("");
  const effectiveCity = selectedCity || availableCities[0] || "";

  const sortedCommunities = useMemo(
    () => [...communities].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [communities]
  );

  const handleToggleDonators = (community: ICommunity) => {
    if (!community._id) return;
    useCommunities.setState((state) => ({
      communities: state.communities.map((c) =>
        c._id === community._id
          ? { ...c, showDonators: !community.showDonators }
          : c
      ),
    }));
    updateCommunity(community._id, {
      ...community,
      showDonators: !community.showDonators,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedCommunities.findIndex((c) => c._id === active.id);
    const newIndex = sortedCommunities.findIndex((c) => c._id === over.id);
    const reordered = arrayMove(sortedCommunities, oldIndex, newIndex);

    reorderCommunities(
      reordered.map((c, i) => ({ id: c._id as string, order: i }))
    );
  };

  if (!communities?.length) return null;

  return (
    <div>
      {availableCities.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <label className="text-sm font-medium text-gray-700">
              Pinning for city
            </label>
            <select
              value={effectiveCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-satoshi focus:outline-none focus:border-droppie-oranje"
            >
              {availableCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-start gap-2 text-xs font-satoshi text-gray-500 bg-gray-50 border border-gray-border rounded-lg px-3 py-2">
            <span className="text-gray-400">ℹ</span>
            <span>
              Pin a community as trending for the city selected above, one
              city at a time. Drag and drop to reorder rows, that order
              applies to every city at once.
            </span>
          </div>
        </div>
      )}
      <div className="relative overflow-x-auto">
        <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
          <thead className="font-medium bg-gray-50">
            <tr>
              <th className="px-2 py-1 sm:py-4 w-8" />
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-center">
                Pinned
              </th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
                Name
              </th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
                Total Pay-out
              </th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
                Cities
              </th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
                Show Donators
              </th>
              <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedCommunities.map((c) => c._id as string)}
                strategy={verticalListSortingStrategy}
              >
                {sortedCommunities.map((community) => (
                  <SortableRow
                    key={community._id}
                    community={community}
                    selectedCity={effectiveCity}
                    onTogglePin={() =>
                      togglePin(
                        community._id as string,
                        effectiveCity,
                        !(community.pinnedCities ?? []).includes(effectiveCity)
                      )
                    }
                    onToggleDonators={() => handleToggleDonators(community)}
                    onOpenDetail={() =>
                      navigate(`/community-detail/${community._id}`)
                    }
                    onEdit={() => navigate(`/edit-community/${community._id}`)}
                    onDelete={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this community?"
                        )
                      ) {
                        deleteCommunity(community._id as string);
                      }
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </tbody>
        </table>
      </div>
    </div>
  );
};
